/**
 * AiDisclosurePanel - "How AI works here" trust disclosure.
 *
 * A small, factual popover summarizing how the assistant operates: the active
 * model, the tenant data boundary, automated verification, workspace-scoped
 * learning, and run auditability. Opened from an info icon in the chat
 * composer toolbar; rendered identically on every chat surface.
 *
 * Copy rules: concise, factual, non-marketing, and white-label - the product
 * ships under multiple brands, so no brand strings here. The model line is the
 * only dynamic part (the label of the currently selected workspace model).
 */

import { IconSize, InfoCircledIcon, Popover, Tooltip } from '@protoss/ui';
import * as React from 'react';

export interface AiDisclosurePanelProps {
  /**
   * Label of the model currently selected in the composer (from the
   * workspace's model list). Omitted while the model list is still loading.
   */
  modelLabel?: string;
}

/** Static disclosure copy - term/description pairs rendered as a <dl>. */
const DISCLOSURE_SECTIONS: ReadonlyArray<{ heading: string; body: string }> = [
  {
    heading: 'Your data',
    body:
      "Retrieval and AI context are limited to this workspace's data. " +
      'Uploaded documents and transcripts stay in your workspace. ' +
      'AI-suggested values are marked and require your acceptance where applicable.',
  },
  {
    heading: 'Verification',
    body:
      'Answers go through automated checks, including schema validation and a quality judge. ' +
      'Low-confidence answers are flagged rather than silently shown.',
  },
  {
    heading: 'Learning',
    body:
      'Corrections you make, such as hours overrides and scope edits, ' +
      'calibrate future outputs for your workspace only.',
  },
  {
    heading: 'Audit',
    body: 'Every AI run is recorded with its steps and can be reviewed in session history.',
  },
];

export function AiDisclosurePanel({ modelLabel }: AiDisclosurePanelProps) {
  const titleId = React.useId();
  return (
    <Popover.Root>
      <Tooltip.Simple label="How AI works here">
        <Popover.Trigger asChild>
          <button type="button" className="ai-disclosure__trigger" aria-label="How AI works here">
            <InfoCircledIcon width={IconSize.sm} height={IconSize.sm} aria-hidden="true" />
          </button>
        </Popover.Trigger>
      </Tooltip.Simple>
      <Popover.Portal>
        <Popover.Content
          className="ai-disclosure__popover"
          side="top"
          align="start"
          sideOffset={6}
          collisionPadding={8}
          aria-labelledby={titleId}
        >
          <div className="ai-disclosure">
            <div className="ai-disclosure__header">
              <h2 className="ai-disclosure__title" id={titleId}>
                How AI works here
              </h2>
              {modelLabel ? (
                <p className="ai-disclosure__model">Powered by {modelLabel}</p>
              ) : null}
            </div>
            <dl className="ai-disclosure__sections">
              {DISCLOSURE_SECTIONS.map((section) => (
                <div key={section.heading} className="ai-disclosure__section">
                  <dt className="ai-disclosure__heading">{section.heading}</dt>
                  <dd className="ai-disclosure__body">{section.body}</dd>
                </div>
              ))}
            </dl>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
